import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import { dashboardService } from '@services';
import { formatGivenName } from '@utils/stringUtils';
import PageLayout from '@components/layout/PageLayout';
import {
  DashboardMetrics,
  StatusChart,
  RecentActivity,
  QuickActions
} from '@components/features/dashboard';
import DashboardHeader from '@components/common/DashboardHeader';
import DashboardGrid from '@components/common/DashboardGrid';
import DashboardRow from '@components/common/DashboardRow';
import DashboardContainer from '@components/common/DashboardContainer';
import DashboardCard from '@components/common/DashboardCard';
import Button from '@components/common/Button';
import ErrorState from '@components/common/ErrorState';

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const permissions = usePermissions();

  // Fetch dashboard data
  const {
    data: dashboardData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['dashboard-metrics', user?.roles, user?.id],
    queryFn: () => {
      if (!user) {
        throw new Error('User not authenticated');
      }
      return dashboardService.getDashboardMetrics(user);
    },
    enabled: !!user, // Only run query if user exists
    refetchInterval: 60000 // Refresh every minute
  });

  if (error) {
    return (
      <PageLayout>
        <ErrorState
          title="Dashboard Unavailable"
          message="Unable to load dashboard data. Please try refreshing the page."
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Refresh Page
            </Button>
          }
        />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <DashboardContainer>
        {/* Welcome Header */}
        <DashboardHeader
          title={`Welcome back, ${user ? formatGivenName(user) : ''}!`}
          subtitle="Here's what's happening with your assessments today."
          role={`Logged in as ${user?.roles?.join(', ') || 'Contact'}`}
          lastUpdated={`Last updated: ${new Date().toLocaleTimeString()}`}
        />

        {/* Main Dashboard Grid */}
        <DashboardGrid>
          {/* Standard Metrics Row */}
          <DashboardRow>
            <DashboardMetrics
              data={
                dashboardData || {
                  assessments: { total: 0, byStatus: [] },
                  suppliers: { total: 0 },
                  contracts: { total: 0 }
                }
              }
              isLoading={isLoading}
            />
          </DashboardRow>

          {/* Assessment Status Distribution Row - STACKED VERTICALLY */}
          {permissions.canAccessAssessments && (
            <DashboardRow>
              <DashboardCard>
                <StatusChart
                  data={dashboardData?.assessments.byStatus || []}
                  isLoading={isLoading}
                  dataTestId="status-chart"
                />
              </DashboardCard>
            </DashboardRow>
          )}

          {/* Quick Actions Row - STACKED VERTICALLY */}
          <DashboardRow>
            <DashboardCard>
              <QuickActions user={user!} />
            </DashboardCard>
          </DashboardRow>

          {/* Recent Activity Row */}
          {permissions.canAccessAssessments && (
            <DashboardRow>
              <DashboardCard>
                <RecentActivity assessments={dashboardData?.recentAssessments || []} isLoading={isLoading} />
              </DashboardCard>
            </DashboardRow>
          )}
        </DashboardGrid>
      </DashboardContainer>
    </PageLayout>
  );
};

export default DashboardPage;
